import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'
import { getWindow } from '@ztechapps/common.utils'

export const MIN_COLUMN_WIDTH = 100

export type UseColumnResizeDragProps = {
  width: number
  /** Measures the column's own live rendered width at the moment the drag starts — the pivot
   * grid's unsized columns pass this because their `width` PROP can lag the DOM (it derives from
   * `measuredWidths`, a grow-only cache that never shrinks back down when a wider row scrolls out
   * of view — `measuredWidths` in `PivotGrid`), so starting from the prop instead of the just-measured
   * DOM box made the first drag move jump by however stale that cache was. `DataTable`'s own
   * `width` is always the authoritative stored/default size (`useColumResize`), never a DOM
   * measurement, so it has no use for this and omits it. */
  getWidth?: (event: React.MouseEvent) => number
  onDrag: (width: number) => void
  onResize: (width: number) => void
}

export type UseColumnResizeDragResult = {
  onStart: (event: React.MouseEvent) => void
}

export const useColumnResizeDrag = ({
  width,
  getWidth,
  onDrag,
  onResize,
}: UseColumnResizeDragProps): UseColumnResizeDragResult => {
  const [isResizing, setIsResizing] = useState(false)
  const originRef = useRef({ startPageX: 0, startWidth: 0, lastWidth: 0, moved: false })
  // Kept current on every render so the document listeners — attached once per drag, not
  // re-attached on every render — still call the latest `onDrag`/`onResize` the caller passed.
  const callbacksRef = useRef({ onDrag, onResize })
  callbacksRef.current = { onDrag, onResize }

  // Document listeners live only while dragging and come off in the effect's cleanup — the same
  // shape as `RouteOffCanvas`'s resize handle — so a plain click, or an unmount mid-drag, never
  // leaves them or the body's cursor and no-select styles behind.
  useEffect(() => {
    if (!isResizing) return
    const doc = getWindow()?.document
    if (!doc) return

    const handleMouseMove = (event: MouseEvent) => {
      const origin = originRef.current
      origin.lastWidth = Math.max(MIN_COLUMN_WIDTH, origin.startWidth + (event.pageX - origin.startPageX))
      origin.moved = true
      callbacksRef.current.onDrag(origin.lastWidth)
    }
    // A save is reported only when the pointer actually moved.
    const handleMouseUp = () => {
      setIsResizing(false)
      if (originRef.current.moved) callbacksRef.current.onResize(originRef.current.lastWidth)
    }

    doc.body.style.cursor = 'col-resize'
    doc.body.style.userSelect = 'none'
    doc.addEventListener('mousemove', handleMouseMove)
    doc.addEventListener('mouseup', handleMouseUp)
    return () => {
      doc.body.style.cursor = ''
      doc.body.style.userSelect = ''
      doc.removeEventListener('mousemove', handleMouseMove)
      doc.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const onStart = useCallback(
    (event: React.MouseEvent) => {
      // Stops the drag from turning into a text selection under the pointer.
      event.preventDefault()
      const startWidth = Math.max(MIN_COLUMN_WIDTH, getWidth ? getWidth(event) : width)
      originRef.current = { startPageX: event.pageX, startWidth, lastWidth: startWidth, moved: false }
      setIsResizing(true)
    },
    [width, getWidth]
  )

  return { onStart }
}
